import React, {
  createContext,
  useReducer,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useDispatch as useReduxDispatch } from 'react-redux';
import appStateReducer from './app-state-reducer';
import * as actionCreators from './app-state-actions';
import { useAuthContext } from '../auth-provider';
import useFetchFromPath from '../../hooks/useFetchFromPath';
import useInterval from '../../hooks/useInterval';
import { convertToMills } from '../../helpers/dateTime';
import useElapsedTimeSinceUpdate from '../../hooks/useElapsedTimeSinceUpdate';
import { setUnreadCatchupCount } from '../../redux/slices/catchup';

export const AppStateContext = createContext(null);
export const AppStateDispatchContext = createContext(null);
export const actions = actionCreators;
const POLL_DELAY = convertToMills(10, 'minutes');

const initialAppState = {
  tour: {
    isOpen: false,
  },
  user: {
    postTargets: null,
    matchRun: null,
  },
  platform: {
    isNativePlatform: Capacitor.isNativePlatform(),
  },
  banners: {
    showGrow: true,
  },
};

export const AppStateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    appStateReducer,
    initialAppState,
  );
  const dispatchRedux = useReduxDispatch();
  const { user } = useAuthContext();

  const {
    results: postTargets,
    loading: targetsLoading,
    refresh: refreshTargets,
  } = useFetchFromPath({ user, route: '/posts/targets' });
  const { elapsedTime: elapsedPostTargetUpdate } = useElapsedTimeSinceUpdate(postTargets);

  const [composedState, setComposedState] = useState(state);

  useEffect(() => {
    setComposedState({
      ...state,
      refreshPostTargets: refreshTargets,
      elapsedPostTargetUpdate,
    });
  }, [state, elapsedPostTargetUpdate]);

  const {
    results: catchupUnreadCount,
    loading: catchupUnreadCountLoading,
    refresh: refreshCatchupUnreadCount,
  } = useFetchFromPath({ user, route: '/user-feed/catchup-unread-count' });

  const [pollDelay, setPollDelay] = useState(null); // value is a number in milliseconds

  const location = useLocation();
  useEffect(() => {
    if (location?.pathname === '/tabs/feed') {
      refreshCatchupUnreadCount();
    }
  }, [location]);

  const pollTargets = useCallback(async () => {
    if (!targetsLoading) {
      refreshTargets();
    }
  }, [user]);
  useInterval(pollTargets, pollDelay);

  React.useEffect(() => {
    if (postTargets) {
      dispatch(actions.setPostTargets(postTargets));
    }
  }, [postTargets]);

  React.useEffect(() => {
    if (catchupUnreadCount && !catchupUnreadCountLoading) {
      dispatchRedux(setUnreadCatchupCount(catchupUnreadCount.count));
    }
  }, [catchupUnreadCount, catchupUnreadCountLoading]);

  React.useEffect(() => {
    if (user) {
      setPollDelay(POLL_DELAY);
    } else {
      dispatch(actions.resetUserState(initialAppState.user));
    }

    return () => {
      dispatch(actions.resetUserState(initialAppState.user));
      setPollDelay(null);
    };
  }, [user]);

  return (
    <AppStateContext.Provider value={composedState}>
      <AppStateDispatchContext.Provider value={dispatch}>
        {children}
      </AppStateDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

export function useAppState() {
  return useContext(AppStateContext);
}

export function useAppStateDispatch() {
  return useContext(AppStateDispatchContext);
}
